<template>
  <v-menu
    offset-y
    min-width="300px"
    :max-height="props.menuHeight"
    :close-on-content-click="false"
    transition="slide-y-transition"
    rounded="xl"
  >
    <template #activator="{ on: menu }">
      <v-tooltip bottom>
        <template #activator="{ on: tooltip }">
          <vs-button
            :label="buildButtonLabel"
            data-test="column-selector-button"
            icon="mdi-view-week"
            type="secondary"
            small
            v-on="{ ...tooltip, ...menu }"
          />
        </template>
        <span>Organize Columns</span>
      </v-tooltip>
    </template>
    <v-card>
      <v-card-title>
        <v-row>
          <v-col>
            <v-text-field v-model="searchValue" dense autofocus hide-details clearable>
              <template #append>
                <v-icon>search</v-icon>
              </template>
            </v-text-field>
          </v-col>
          <v-col class="shrink">
            <v-switch
              v-model="showHiddenColumns"
              class="mt-0"
              inset
              label="Show hidden"
              hide-details
              dense
            />
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-actions>
        <vs-button
          type="secondary"
          class="mr-2"
          small
          shrink
          :class="['option', allCoreColumnsSelected ? 'option--active' : '']"
          label="Core fields"
          @click="toggleCoreColumns"
        />
        <vs-button
          type="secondary"
          class="mr-2"
          small
          shrink
          :class="['option', allCustomColumnsSelected ? 'option--active' : '']"
          label="Custom fields"
          @click="toggleCustomColumns"
        />
        <span class="reset-button" role="button" small @click="resetColumns">Reset</span>
      </v-card-actions>
      <v-data-table
        hide-default-header
        item-key="value"
        :items="orderedColumns"
        disable-pagination
        hide-default-footer
      >
        <template #body="{ items }">
          <component
            :is="searchValue ? 'tbody' : draggable"
            v-model="orderedColumnIdsModel"
            tag="tbody"
            ghost-class="color-grey--lightest"
            animation="300"
            handle=".sortable"
          >
            <tr
              v-for="column in items"
              :class="{
                hidden: !searchValue && showHiddenColumns ? false : !columnList.includes(column),
              }"
              @click="toggleColumnVisibility(column)"
            >
              <td style="width: 24px">
                <v-simple-checkbox
                  color="primary"
                  :value="visibleColumns.includes(column.value)"
                  @click="toggleColumnVisibility(column)"
                ></v-simple-checkbox>
              </td>
              <td>{{ column.text }}</td>
              <td v-if="!searchValue" style="width: 24px">
                <v-icon class="sortable" style="cursor: pointer"> drag_indicator </v-icon>
              </td>
            </tr>
          </component>
        </template>
        <template #no-results>Nothing found</template>
      </v-data-table>
    </v-card>
  </v-menu>
</template>

<script setup lang="ts">
import { useStore } from 'vuex-composition-helpers'
import { computed, ref } from 'vue'
import draggable from 'vuedraggable'

const props = withDefaults(
  defineProps<{
    menuHeight?: string
  }>(),
  {
    menuHeight: '90vh',
  },
)
const searchValue = ref<string | null>(null)
const showHiddenColumns = ref(true)
const store = useStore()
const orderedColumns = computed<
  {
    text: string
    value: never
  }[]
>(() => store.getters['jobsList/getOrderedColumns'])
const visibleColumns = computed<string[]>(() => store.getters['jobsList/getVisibleColumnIds'])
const allCoreColumnsSelected = computed<boolean>(
  () => store.getters['jobsList/allCoreColumnsSelected'],
)
const allCustomColumnsSelected = computed<boolean>(
  () => store.getters['jobsList/allCustomColumnsSelected'],
)
const buildButtonLabel = computed(
  () => `${visibleColumns.value.length} of ${orderedColumns.value.length}`,
)
const columnList = computed(() =>
  orderedColumns.value.filter(
    (c) =>
      (showHiddenColumns.value || visibleColumns.value.includes(c.value)) &&
      (!searchValue.value || c.text.toLowerCase().includes(searchValue.value?.toLowerCase())),
  ),
)
const orderedColumnIdsModel = computed({
  get() {
    return orderedColumns.value
  },
  set(columnOrder: { text: string; value: never }[]) {
    setColumnOrdering(columnOrder.map((column) => column.value))
  },
})
const setVisibleColumns = (arg: string[]) => {
  store.dispatch('jobsList/updateVisibleColumns', arg)
}
const setColumnOrdering = (arg: string[]) => {
  store.dispatch('jobsList/updateColumnOrder', arg)
}
const toggleCoreColumns = () => {
  store.dispatch('jobsList/toggleCoreColumns')
}
const toggleCustomColumns = () => {
  store.dispatch('jobsList/toggleCustomColumns')
}
const resetColumns = () => {
  store.dispatch('jobsList/resetColumns')
}
const toggleColumnVisibility = (column: { text: string; value: never }) => {
  const index = visibleColumns.value.indexOf(column.value)
  if (index === -1) {
    setVisibleColumns(visibleColumns.value.concat([column.value]))
  } else {
    const columns = [...visibleColumns.value]
    columns.splice(index, 1)
    setVisibleColumns(columns)
  }
}

defineExpose({ buildButtonLabel }) // For unit test
</script>

<style scoped>
.vs-button.option {
  height: 2rem;
  font-weight: 500;
  font-family: var(--font-regular);
  position: relative;
  color: var(--color-black);
  border: thin solid var(--color-grey--lighter);
  background-color: transparent;
}

.vs-button.option--active {
  color: var(--color-blue);
}

.option.option--active:before {
  opacity: 0.08;
}

.option:before {
  content: '';
  background-color: currentColor;
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  border-radius: inherit;
}

.reset-button {
  font-size: 0.75rem;
  height: 1.5rem;
  margin: 4px 8px 4px 0;
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  line-height: 1.25;
  max-width: 100%;
  padding: 0 0.75em;
  position: relative;
  text-decoration: none;
  transition-duration: 0.28s;
  transition-property: box-shadow, opacity;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  vertical-align: middle;
  white-space: nowrap;
}

tr.hidden {
  display: none;
}

td {
  max-width: 100%;
  white-space: nowrap;
}
</style>
